'use client';

import { VoteType } from '@prisma/client';
import {
  createContext,
  ReactNode,
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useState,
  useRef
} from 'react';
import { CommentData } from '../../../types/shared';

type RealtimeContextType = {
  likes: number;
  dislikes: number;
  myVote: VoteType | null;
  comments: CommentData[];
  addOptimisticComment: (comment: CommentData) => void;
  deleteOptimisticComment: (commentId: string) => void;
  optimisticVoteUpdate: (voteType: VoteType) => void;
};

const RealtimeContext = createContext<RealtimeContextType | undefined>(undefined);

// Функция для обновления голоса (чистая логика)
function calcVote(
  current: { likes: number; dislikes: number; myVote: VoteType | null },
  voteType: VoteType,
) {
  let { likes, dislikes, myVote } = current;

  if (myVote === voteType) {
    if (voteType === 'LIKE') likes = Math.max(0, likes - 1);
    if (voteType === 'DISLIKE') dislikes = Math.max(0, dislikes - 1);
    return { likes, dislikes, myVote: null };
  }

  if (myVote === 'LIKE') likes = Math.max(0, likes - 1);
  if (myVote === 'DISLIKE') dislikes = Math.max(0, dislikes - 1);

  if (voteType === 'LIKE') likes++;
  if (voteType === 'DISLIKE') dislikes++;

  return { likes, dislikes, myVote: voteType };
}

function collectDescendantIds(comments: CommentData[], rootId: string) {
  const childrenMap = new Map<string, string[]>();
  for (const comment of comments) {
    if (!comment.parentId) continue;
    const list = childrenMap.get(comment.parentId);
    if (list) list.push(comment.id);
    else childrenMap.set(comment.parentId, [comment.id]);
  }

  const ids = new Set<string>([rootId]);
  const queue = [rootId];
  while (queue.length > 0) {
    const current = queue.shift();
    if (!current) continue;
    const children = childrenMap.get(current) ?? [];
    for (const childId of children) {
      if (!ids.has(childId)) {
        ids.add(childId);
        queue.push(childId);
      }
    }
  }
  return ids;
}

export function RealtimeProvider({
  children,
  photoId,
  initialData,
}: {
  children: ReactNode;
  photoId: string;
  initialData: {
    likes: number;
    dislikes: number;
    myVote: VoteType | null;
    comments: CommentData[];
  };
}) {
  const [comments, setComments] = useState(initialData.comments);

  const addOptimisticComment = useCallback((newComment: CommentData) => {
    setComments((prev) => {
      if (prev.some((c) => c.id === newComment.id)) return prev;
      return [...prev, newComment];
    });
  }, []);

  const deleteOptimisticComment = useCallback((commentId: string) => {
    setComments((prev) => {
      const idsToDelete = collectDescendantIds(prev, commentId);
      return prev.filter((comment) => !idsToDelete.has(comment.id));
    });
  }, []);

  const [stats, setStats] = useState({
    likes: initialData.likes,
    dislikes: initialData.dislikes,
    myVote: initialData.myVote,
  });

  const initRef = useRef(initialData);
  initRef.current = initialData;

  // ⚠️ Важно: ресет состояния только при смене фото,
  // чтобы не перетирать оптимистичные/real-time обновления на каждый новый объект props.

  useEffect(() => {
    const init = initRef.current;
    setStats({
      likes: init.likes,
      dislikes: init.dislikes,
      myVote: init.myVote,
    });
    setComments(init.comments);
  }, [photoId]);

  const optimisticVoteUpdate = useCallback((voteType: VoteType) => {
    setStats((prev) => calcVote(prev, voteType));
  }, []);

  const contextValue = useMemo(
    () => ({
      likes: stats.likes,
      dislikes: stats.dislikes,
      myVote: stats.myVote,
      comments,
      addOptimisticComment,
      deleteOptimisticComment,
      optimisticVoteUpdate,
    }),
    [stats, comments, addOptimisticComment, deleteOptimisticComment, optimisticVoteUpdate],
  );

  return <RealtimeContext.Provider value={contextValue}>{children}</RealtimeContext.Provider>;
}

export function useRealtime() {
  const context = useContext(RealtimeContext);
  if (!context) throw new Error('useRealtime must be used within a RealtimeProvider');
  return context;
}
